import api from '@/services/api';
import type {
    AddNoteInput,
    AgreementTimelineEntry,
    NotesPage,
} from '../types/agreementTimeline.types';
import { readString } from '../utils/snapshotReaders';
import { readTimelineMeta } from '../utils/timelineMeta';

export const NOTES_PER_PAGE = 25;

type RawNode = Record<string, unknown>;

const asString = (value: unknown): string | null => readString(value) ?? null;

const isRecord = (value: unknown): value is RawNode =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const mapBase = (raw: RawNode) => ({
    id: String(raw.id ?? ''),
    occurredAt: String(raw.occurred_at ?? ''),
    actorType: asString(raw.actor_type),
    actorId: asString(raw.actor_id),
    actorName: asString(raw.actor_name),
    crmCaseId: asString(raw.crm_case_id),
});

export const mapTimelineNode = (raw: RawNode): AgreementTimelineEntry => {
    const base = mapBase(raw);
    const entryType = String(raw.entry_type ?? '');

    if (entryType === 'note' && isRecord(raw.note)) {
        return {
            ...base,
            kind: 'note',
            note: {
                id: String(raw.note.id ?? ''),
                noteType: String(raw.note.note_type ?? ''),
                body: String(raw.note.body ?? ''),
                crmCaseId: asString(raw.note.crm_case_id),
            },
        };
    }

    return { ...base, kind: 'unknown', entryType };
};

/** Order is the server's; entries are never re-sorted on the client. */
export const fetchAgreementNotes = async (
    id: string,
    params: { page: number; perPage: number },
): Promise<NotesPage> => {
    const raw = (
        await api.get(`/agreements/${id}/notes`, { params: { page: params.page, per_page: params.perPage } })
    ).data;
    const meta = isRecord(raw.meta) ? raw.meta : {};

    return {
        entries: ((raw.data ?? []) as RawNode[]).map(mapTimelineNode),
        meta: readTimelineMeta(meta, params),
    };
};

export const addAgreementNote = async (id: string, input: AddNoteInput): Promise<AgreementTimelineEntry> => {
    const crmCaseId = input.crmCaseId?.trim();
    const payload = {
        note_type: input.noteType,
        body: input.body,
        ...(crmCaseId ? { crm_case_id: crmCaseId } : {}),
    };

    return mapTimelineNode((await api.post(`/agreements/${id}/notes`, payload)).data.data);
};
